import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { Subscription, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs';
import { HighlightPropertyModel } from 'src/app/models/highlights-property.model';
import { SearchModel } from 'src/app/models/search.model';
import { EventService } from 'src/app/services/event.service';
import { LoadingService } from 'src/app/services/loading.service';
import { PropertyService } from 'src/app/services/property.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-home-mobile',
  templateUrl: './home-mobile.component.html',
  styleUrls: ['./home-mobile.component.css'],
})
export class HomeMobileComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() highlightsProperties!: HighlightPropertyModel[];
  @Input() localAccommodations: HighlightPropertyModel[] = [];
  popularCities!: any[];
  @Input() searchData!: any;
  @Input() latestSearches: any[] = [];
  @Input() networkStatus: any;
  queryField = new FormControl();
  searchResult: any[] = [];
  searchForm!: SearchModel;
  moreChar: boolean = false;
  areaValue = 0;
  areaMaxValue = 1300;
  priceValue = 0;
  priceMaxValue = 1300000;
  clearSearch: {
    isClear: boolean;
    result: any;
  } = {
      isClear: false,
      result: {},
    };
  county: any[] = [];
  parish: any[] = [];
  selectedDistrict: any;
  selectedCounty: any;
  selectedParish: any;
  placeholder: boolean = false;
  priceChange: boolean = false;
  areaChange: boolean = false;
  subscriptions: Subscription = new Subscription();
  homeTexts: boolean = environment.homeTexts;

  constructor(
    private offCanvasService: NgbOffcanvas,
    private eventService: EventService,
    private router: Router,
    private propertyService: PropertyService,
    private dbService: NgxIndexedDBService,
    private loadingService: LoadingService
  ) { }

  ngOnInit(): void {
    this.getLocations();
    this.loadingService.placeholderValue.subscribe(
      (value) => (this.placeholder = value)
    );
    this.searchLocations();
  }

  ngAfterViewInit(): void {
    const interval = setInterval(() => {
      if (this.searchData) {
        this.areaMaxValue = this.searchData?.areaMax;
        this.priceMaxValue = this.searchData?.priceMax;
        clearInterval(interval);
      }
    }, 200);
  }

  getLocations() {
    if (this.networkStatus === 1) {
      let entityData: any = localStorage.getItem('entityData');
      if (entityData)
        entityData = JSON.parse(entityData);
      if (entityData?.dynamicPlaces && sessionStorage.getItem('popularCities') !== null) {
        const data: any = sessionStorage.getItem('popularCities');
        this.popularCities = JSON.parse(data);
      } else {
        const locationsSub = this.propertyService.getPopularLocations().subscribe({
          next: data => {
            this.popularCities = data;
            sessionStorage.setItem('popularCities', JSON.stringify(data));
            this.dbService.clear('latest-popular-cities').subscribe({
              next: () => {
                this.dbService.add('latest-popular-cities', data).subscribe();
              },
              error: err => {
                if (err.startsWith('objectStore does not exists')) {
                  this.dbService.createObjectStore({
                    store: 'latest-popular-cities',
                    storeConfig: { keyPath: 'id', autoIncrement: true },
                    storeSchema: [],
                  });
                  this.dbService.add('latest-popular-cities', data).subscribe();
                }
              }
            })
          }
        });
        this.subscriptions.add(locationsSub);
      }
    } else {
      let entityData: any = localStorage.getItem('entityData');
      if (entityData)
        entityData = JSON.parse(entityData);
      if (entityData?.dynamicPlaces && sessionStorage.getItem('popularCities') !== null) {
        const data: any = sessionStorage.getItem('popularCities');
        this.popularCities = JSON.parse(data);
      } else {
        this.dbService.getAll('latest-popular-cities').subscribe({
          next: (data: any[]) => {
            if (data && data.length > 0) {
              this.popularCities = data[0];
            }
          }
        })
      }
    }
  }

  inputChange(type: 'area' | 'price') {
    if (type === 'area') {
      this.areaChange = true;
      this.areaValue > this.areaMaxValue
        ? (this.areaMaxValue = this.areaValue)
        : null;
    } else {
      this.priceChange = true;
      this.priceValue > this.priceMaxValue
        ? (this.priceMaxValue = this.priceValue)
        : null;
    }
  }

  districtChange() {
    this.selectedCounty = null;
    this.selectedParish = null;
    this.county = [];
    this.parish = [];
    if (this.selectedDistrict && this.selectedDistrict != '') {
      let selected = this.selectedDistrict.split('-');
      const country = selected[0];
      const district = selected[1];
      const region = selected[2];
      for (const [key, value] of Object.entries<any>(
        this.searchData?.locations
      )) {
        if (
          value.countryId == country &&
          value.id == district &&
          value.regionId == region
        ) {
          value.counties.forEach((county: any) => {
            this.county.push(county);
          });
        }
      }
    } else {
      this.selectedCounty = null;
      this.selectedParish = null;
    }
  }

  countyChange() {
    this.selectedParish = null;
    this.parish = [];
    this.county.forEach((c) => {
      if (c.id == this.selectedCounty) {
        c.parishs.forEach((parish: any) => {
          this.parish.push(parish);
        });
      }
    });
  }

  searchLocations() {
    this.queryField.valueChanges
      .pipe(
        map((value) => value.trim().toLowerCase()),
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((value) =>
          value != ''
            ? this.propertyService.getLocations(value)
            : (this.searchResult = [])
        )
      )
      .subscribe((result) => {
        this.searchResult = result;
      });
  }

  clearLastSearch(key: any) {
    this.dbService.deleteByKey('latest-searches', key).subscribe({
      error: (err: string) => {
        if (err.startsWith('objectStore does not exists')) {
          this.dbService.createObjectStore({
            store: 'latest-searches',
            storeConfig: { keyPath: 'searchKey', autoIncrement: false },
            storeSchema: [],
          });
        }
      },
    });
    this.latestSearches = this.latestSearches.filter(
      (el) => el.searchKey !== key
    );
    this.clearSearch.isClear = false;
  }

  searchWithLatest(content: any, search: any, type: 'location' | 'freeSearch') {
    if (!this.clearSearch.isClear) {
      this.closeOffCanvas(content);
      type === 'freeSearch'
        ? this.router.navigate(['/properties'], {
          queryParams: { freeSearch: search, lastSearch: true },
        })
        : this.router.navigate(['/properties'], {
          queryParams: { location: search, lastSearch: true },
        });
    } else {
      this.clearLastSearch(this.clearSearch.result);
    }
  }

  searchByLocation(id: number, content: any) {
    this.closeOffCanvas(content);
    this.dbService
      .add('latest-searches', {
        searchKey: JSON.stringify(id),
        type: 'location',
        name: this.queryField.value,
      })
      .subscribe({
        error: (err: string) => {
          if (err.startsWith('objectStore does not exists')) {
            this.dbService.createObjectStore({
              store: 'latest-searches',
              storeConfig: { keyPath: 'searchKey', autoIncrement: false },
              storeSchema: [],
            });
          }
        },
      });
    this.router.navigate(['/properties'], {
      queryParams: { location: JSON.stringify(id) },
    });
  }

  generalSearch(content: any) {
    this.dbService
      .add('latest-searches', {
        searchKey: this.queryField.value,
        type: 'freeSearch',
      })
      .subscribe({
        error: (err: string) => {
          if (err.startsWith('objectStore does not exists')) {
            this.dbService.createObjectStore({
              store: 'latest-searches',
              storeConfig: { keyPath: 'searchKey', autoIncrement: false },
              storeSchema: [],
            });
          }
        },
      });
    this.closeOffCanvas(content);
    this.router.navigate(['/properties'], {
      queryParams: { freeSearch: this.queryField.value },
    });
  }

  initForm() {
    this.searchForm = {
      businessType: null,
      propertyType: null,
      propertyCondition: null,
      bathrooms: null,
      bedrooms: null,
      areaMin: null,
      areaMax: null,
      priceMin: null,
      priceMax: null,
      characteristics: [],
      location: null,
      reference: null,
    };
  }

  prepareSearchData() {
    document
      .querySelectorAll('.beds-items button')
      .forEach((el) =>
        el.classList.contains('active')
          ? (this.searchForm.bedrooms = el.getAttribute('value'))
          : null
      );
    document
      .querySelectorAll('.bath-items button')
      .forEach((el) =>
        el.classList.contains('active')
          ? (this.searchForm.bathrooms = el.getAttribute('value'))
          : null
      );
    if (this.selectedDistrict) {
      let selected = this.selectedDistrict.split('-');
      if (this.selectedCounty) {
        if (this.selectedParish) {
          for (const [key, value] of Object.entries<any>(
            this.searchData.locations
          )) {
            if (
              value.countryId == selected[0] &&
              value.id == selected[1] &&
              value.regionId == selected[2]
            ) {
              for (const [key1, value1] of Object.entries<any>(
                value.counties
              )) {
                if (value1.id == this.selectedCounty) {
                  for (const [key2, value2] of Object.entries<any>(
                    value1.parishs
                  )) {
                    if (value2.id == this.selectedParish) {
                      this.searchForm.location = {
                        country: value.countryId,
                        region: value.regionId,
                        district: value.id,
                        county: value1.id,
                        parish: value2.id,
                      };
                    }
                  }
                }
              }
            }
          }
        } else {
          for (const [key, value] of Object.entries<any>(
            this.searchData.locations
          )) {
            if (
              value.countryId == selected[0] &&
              value.id == selected[1] &&
              value.regionId == selected[2]
            ) {
              for (const [key1, value1] of Object.entries<any>(
                value.counties
              )) {
                if (value1.id == this.selectedCounty) {
                  this.searchForm.location = {
                    country: value.countryId,
                    region: value.regionId,
                    district: value.id,
                    county: value1.id
                  };
                }
              }
            }
          }
        }
      } else {
        for (const [key, value] of Object.entries<any>(
          this.searchData.locations
        )) {
          if (
            value.countryId == selected[0] &&
            value.id == selected[1] &&
            value.regionId == selected[2]
          ) {
            this.searchForm.location = {
              country: value.countryId,
              region: value.regionId,
              district: value.id
            };
          }
        }
      }
    }
    this.searchData.characteristics.forEach((char: any) => {
      const el: any = document.getElementById(`check-char-${char.id}`);
      el?.checked ? this.searchForm.characteristics?.push(char.id) : null;
    });
  }

  searchWithAdvanced(content: any) {
    this.prepareSearchData();
    this.closeOffCanvas(content);
    const params = {
      typografy:
        this.searchForm.bedrooms != '' ? this.searchForm.bedrooms : null,
      bathrooms:
        this.searchForm.bathrooms != '' ? this.searchForm.bathrooms : null,
      businessType: this.searchForm.businessType,
      propertyType: this.searchForm.propertyType,
      propertyCondition: this.searchForm.propertyCondition,
      priceMin: this.priceChange ? this.priceValue : null,
      priceMax: this.priceChange ? this.priceMaxValue : null,
      areaMin: this.areaChange ? this.areaValue : null,
      areaMax: this.areaChange ? this.areaMaxValue : null,
      characteristics:
        this.searchForm?.characteristics?.length > 0
          ? JSON.stringify(this.searchForm.characteristics)
          : null,
      location: this.searchForm?.location
        ? JSON.stringify(this.searchForm.location)
        : null,
      reference: this.searchForm?.reference
        ? this.searchForm.reference.trim()
        : null,
    };
    this.router.navigate(['properties'], { queryParams: params });
  }

  clearFilters() {
    this.areaMaxValue = 1300;
    this.areaValue = 0;
    this.priceMaxValue = 1300000;
    this.priceValue = 0;
    this.selectedParish = null;
    this.parish = [];
    this.selectedCounty = null;
    this.county = [];
    this.selectedDistrict = null;
  }

  openOffCanvas(content: any) {
    this.initForm();
    this.eventService.broadcast('stopEventScroll');
    this.offCanvasService.open(content, { position: 'bottom' });
  }

  closeOffCanvas(content: any) {
    this.eventService.broadcast('restartEventScroll');
    this.offCanvasService.dismiss(content);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
